import { TFunction } from 'i18next'
import { SocialMarketFitReason, SocialTopicPostItem } from '@digital-twins/shared-types'
import { localizedCountryName } from '@/lib/utils/localized-country-name'
import { formatSocialLanguages } from './socialFormat'

type CountryReasonKey = 'account' | 'channel' | 'geotags' | 'bio' | 'postGeotag' | 'community'

/** Reasons that name the country that decided the verdict. */
const COUNTRY_REASON_KEY: Partial<Record<SocialMarketFitReason, CountryReasonKey>> = {
  author_account: 'account',
  author_channel: 'channel',
  author_geotags: 'geotags',
  author_bio: 'bio',
  post_geotag: 'postGeotag',
  subreddit: 'community',
}

/** Reasons read from the post itself (the judge), with no country to name. */
const JUDGED_REASONS: ReadonlyArray<SocialMarketFitReason> = [
  'judged',
  'judge_no',
  'judge_country',
  'author_judge_country',
]

/**
 * Why a post sits in or out of its Topic's market, as one short line ("Account
 * based in Brazil"); null when the Topic has no market and no languages (no
 * verdict) or the server sends no market fit.
 */
export function socialMarketReasonText(
  post: Pick<SocialTopicPostItem, 'marketReason' | 'marketCountry' | 'language'>,
  t: TFunction<'app'>,
  uiLanguage: string,
): string | null {
  const reason = post.marketReason
  if (!reason || reason === 'no_market') {
    return null
  }
  const countryKey = COUNTRY_REASON_KEY[reason]
  if (countryKey) {
    return post.marketCountry
      ? t(`socialListening.market.reason.${countryKey}`, {
          country: localizedCountryName(post.marketCountry, uiLanguage),
        })
      : t('socialListening.market.reason.none')
  }
  if (reason === 'abroad') {
    return t('socialListening.market.reason.abroad')
  }
  if (reason === 'language' && post.language) {
    return t('socialListening.market.reason.language', {
      language: formatSocialLanguages([post.language.split('-')[0]], uiLanguage),
    })
  }
  if (reason === 'language' || JUDGED_REASONS.includes(reason)) {
    return t('socialListening.market.reason.judged')
  }
  return t('socialListening.market.reason.none')
}

/** The country a post's chip names (localized), when an author, a tag or a community placed it there. */
export function socialMarketCountryLabel(
  post: Pick<SocialTopicPostItem, 'marketReason' | 'marketCountry'>,
  uiLanguage: string,
): string | null {
  if (!post.marketCountry || !post.marketReason || !COUNTRY_REASON_KEY[post.marketReason]) {
    return null
  }
  return localizedCountryName(post.marketCountry, uiLanguage)
}
